import { useCallback, useEffect, useRef, useState } from 'react';

import type { RefObject } from 'react';

export type UseInfiniteScrollParams = {
  onReachEnd: () => void;
  root?: RefObject<Element | null>;
  rootMargin?: string;
  disabled?: boolean;
};

export function useInfiniteScroll({
  onReachEnd,
  root,
  rootMargin = '120px',
  disabled = false,
}: UseInfiniteScrollParams) {
  const [node, setNode] = useState<Element | null>(null);

  const onReachEndRef = useRef(onReachEnd);
  onReachEndRef.current = onReachEnd;

  useEffect(() => {
    if (disabled || !node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onReachEndRef.current();
        }
      },
      { root: root?.current ?? null, rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, root, rootMargin, disabled]);

  return useCallback((element: Element | null) => setNode(element), []);
}
